import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import Layout from "../../components/Layout";
import api from "../../lib/api";
import DOMPurify from "dompurify";
import DataAudit from "./_dataAudit";
import LinkedRecords from "./LinkedRecords";
import { productTypeLabel } from "../../lib/productEnums";
import { ProductStatusPill } from "../../components/ProductStatusPill";
import { RawJson } from "./_shared";
import {
  ArrowLeft, Package, Tag, Boxes, ShoppingBag, Store, Image as ImgIcon,
  Search as SearchIcon, FileText, Truck, Layers, Settings2, Building2, ExternalLink,
  BadgePercent,
} from "lucide-react";

/* ------------------------ small utility helpers ------------------------ */
const money = (n, cur = "£") => {
  if (n === null || n === undefined || n === "") return "—";
  const v = Number(n);
  if (Number.isNaN(v)) return String(n); // already-formatted "£27.23" strings survive
  return `${cur}${v.toFixed(2)}`;
};
const asText = (v) => (v === null || v === undefined || v === "" ? "—" : String(v));
const yesNo = (v) => (v === true ? "Yes" : v === false ? "No" : "—");

/* ------------------------ atomic UI atoms ------------------------ */
function Panel({ icon: Icon, title, right, children, testid }) {
  return (
    <section
      className="rounded-xl border border-white/10 bg-[#0F1420]/70 overflow-hidden"
      data-testid={testid}
    >
      <header className="flex items-center justify-between px-4 py-3 border-b border-white/10 bg-white/[0.02]">
        <div className="flex items-center gap-2">
          {Icon && <Icon className="w-4 h-4 text-orange-400" />}
          <h3 className="text-[11px] font-bold uppercase tracking-widest text-orange-400 font-mono">
            {title}
          </h3>
        </div>
        {right && <div className="text-[11px] font-mono text-slate-500">{right}</div>}
      </header>
      <div className="p-4">{children}</div>
    </section>
  );
}

function KV({ label, value, mono = false, wide = false }) {
  return (
    <div className={wide ? "col-span-full" : ""}>
      <div className="text-[10px] uppercase tracking-widest text-slate-500 font-mono">{label}</div>
      <div className={`text-sm mt-0.5 text-slate-100 ${mono ? "font-mono" : ""} break-words`}>
        {value ?? "—"}
      </div>
    </div>
  );
}

function Chip({ children, tone = "slate" }) {
  const map = {
    slate:   "bg-white/5 text-slate-200 border-white/10",
    orange:  "bg-orange-500/10 text-[#C8A46B] border-orange-500/30",
    green:   "bg-emerald-500/10 text-emerald-300 border-emerald-500/30",
    red:     "bg-red-500/10 text-red-300 border-red-500/30",
    blue:    "bg-blue-500/10 text-blue-300 border-blue-500/30",
    violet:  "bg-violet-500/10 text-violet-300 border-violet-500/30",
    warn:    "bg-amber-500/10 text-amber-300 border-amber-500/30",
  };
  return (
    <span className={`inline-flex items-center gap-1 text-[11px] px-2 py-1 rounded-md border font-mono ${map[tone]}`}>
      {children}
    </span>
  );
}

/* ------------------------ page ------------------------ */
export default function LabProductDetail() {
  const { id } = useParams();
  const [doc, setDoc] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(null);
  const [tierPricing, setTierPricing] = useState(null);

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        setLoading(true);
        const { data } = await api.get(`/lab/products/${id}`);
        if (alive) setDoc(data);
      } catch (e) {
        console.error(e);
        if (alive) setErr(e?.response?.data?.detail || "Failed to load product");
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => { alive = false; };
  }, [id]);

  useEffect(() => {
    let alive = true;
    api.get(`/pricing/product-tiers/${id}`)
      .then(({ data }) => { if (alive) setTierPricing(data); })
      .catch(() => { if (alive) setTierPricing(null); });
    return () => { alive = false; };
  }, [id]);

  if (loading) {
    return <Layout><div className="text-center text-slate-500 py-20">Loading product…</div></Layout>;
  }
  if (err || !doc) {
    return (
      <Layout>
        <div className="max-w-xl mx-auto text-center py-20">
          <div className="text-red-400 mb-3">{err || "Product not found"}</div>
          <Link to="/lab/products" className="text-orange-400 underline">Back to Data Lab → Products</Link>
        </div>
      </Layout>
    );
  }

  const bc = doc.BCProduct || {};
  const tm = doc.TMProduct || {};
  const receivedAt = doc._received_at;

  const desc = bc.description ? DOMPurify.sanitize(bc.description) : "";
  // BC `price` is tax-INCLUSIVE → derive true ex-VAT (÷1.2) and base margin on it.
  const VAT_RATE = 0.20;
  const sellExVat = (Number(bc.price) > 0) ? Number(bc.price) / (1 + VAT_RATE) : null;
  const marginPct = (sellExVat !== null && Number(bc.cost_price) >= 0)
    ? (((sellExVat - Number(bc.cost_price)) / sellExVat) * 100)
    : null;
  const avgRating = (Number(bc.reviews_count) > 0)
    ? (Number(bc.reviews_rating_sum) / Number(bc.reviews_count))
    : null;

  const listedTone = (v) => (v ? "green" : "slate");

  return (
    <Layout>
      <div className="space-y-6" data-testid="lab-product-detail-page">
        {/* Back link + Eyebrow */}
        <div className="flex items-center gap-2 text-sm">
          <Link to="/lab/products" className="text-slate-400 hover:text-white inline-flex items-center gap-1">
            <ArrowLeft className="w-4 h-4" /> Data Lab · Products
          </Link>
        </div>

        {/* HEADER */}
        <div className="rounded-xl border border-white/10 bg-gradient-to-br from-[#0F1420] via-[#0F1420] to-[#1a1223] p-6">
          <div className="flex items-start justify-between gap-4 flex-wrap">
            <div className="min-w-0">
              <div className="flex items-center gap-2 mb-2">
                <Chip tone="orange">SKU · {asText(bc.sku)}</Chip>
                {tm.BrandName && <Chip tone="violet">{tm.BrandName}</Chip>}
                {tm.ProductLabel && <Chip tone="blue">{tm.ProductLabel}</Chip>}
                {bc.is_featured && <Chip tone="warn">★ Featured</Chip>}
                {bc.availability && <Chip tone={bc.availability === "available" ? "green" : "warn"}>{bc.availability}</Chip>}
              </div>
              <h1 className="text-2xl sm:text-3xl font-bold text-white leading-tight break-words">
                {asText(bc.name)}
              </h1>
              <div className="flex flex-wrap items-center gap-x-6 gap-y-1 mt-3 text-xs text-slate-400 font-mono">
                <span>BC #{asText(bc._id)}</span>
                <span>TM #{asText(tm.ProductId)}</span>
                {bc.upc && <span>UPC {bc.upc}</span>}
                {bc.mpn && <span>MPN {bc.mpn}</span>}
                {bc.bin_picking_number && <span>BIN {bc.bin_picking_number}</span>}
                {receivedAt && <span>Synced {new Date(receivedAt).toLocaleString("en-GB")}</span>}
              </div>
            </div>
            <div className="text-right">
              <div className="text-[11px] uppercase tracking-widest text-slate-500 font-mono">Sell (ex VAT)</div>
              <div className="text-4xl font-bold font-mono text-orange-400">{money(sellExVat)}</div>
              <div className="text-[11px] text-slate-500 mt-0.5 font-mono">{money(bc.price)} inc VAT</div>
              {marginPct !== null && (
                <div className="text-xs text-slate-400 mt-1 font-mono">
                  Margin <span className={marginPct >= 20 ? "text-emerald-400" : marginPct >= 13 ? "text-amber-400" : "text-red-400"}>
                    {marginPct.toFixed(1)}%
                  </span>
                </div>
              )}
            </div>
          </div>

          {/* Stat strip */}
          <div className="mt-5 pt-4 border-t border-white/5 grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-6 gap-4">
            <KV label="Stock qty"     value={<span className="font-mono">{asText(bc.inventory_level)}</span>} />
            <KV label="Total sold"    value={<span className="font-mono">{asText(bc.total_sold)}</span>} />
            <KV label="Page views"    value={<span className="font-mono">{asText(bc.view_count)}</span>} />
            <KV label="Reviews"       value={<span className="font-mono">{bc.reviews_count || 0} ★ {avgRating !== null ? avgRating.toFixed(1) : "—"}</span>} />
            <KV label="Categories"    value={<span className="font-mono">{(bc.categories || []).length}</span>} />
            <KV label="Variants"      value={<span className="font-mono">{(bc.variants || []).length}</span>} />
          </div>
        </div>

        {/* 2-col layout */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* LEFT (main) */}
          <div className="lg:col-span-2 space-y-6">
            {/* Pricing */}
            <Panel icon={Tag} title="Pricing" testid="panel-pricing">
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
                <KV label="Cost price"    value={<span className="font-mono">{money(bc.cost_price)}</span>} />
                <KV label="Sell ex VAT"   value={<span className="font-mono text-[#C8A46B]">{money(sellExVat)}</span>} />
                <KV label="Sell inc VAT"  value={<span className="font-mono">{money(bc.price)}</span>} />
                <KV label="Retail (MSRP)" value={<span className="font-mono">{money(bc.retail_price)}</span>} />
                <KV label="Sale price"    value={<span className="font-mono">{Number(bc.sale_price) > 0 ? money(bc.sale_price) : "—"}</span>} />
                <KV label="Calculated"    value={<span className="font-mono">{money(bc.calculated_price)}</span>} />
                <KV label="MAP price"     value={<span className="font-mono">{Number(bc.map_price) > 0 ? money(bc.map_price) : "—"}</span>} />
                <KV label="MM inc VAT"    value={<span className="font-mono">{asText(tm.MMPriceIncVAT)}</span>} />
                <KV label="MM sales unit" value={<span className="font-mono">{asText(tm.MMSalesUnitPriceIncVAT)}</span>} />
                <KV label="IS inc VAT"    value={<span className="font-mono">{asText(tm.ISPriceIncVAT)}</span>} />
              </div>
              {/* How these fields flow into the Quote Builder (read-only map) */}
              <div className="mt-4 pt-4 border-t border-white/5" data-testid="pricing-mapping">
                <div className="text-[10px] uppercase tracking-widest text-slate-500 font-mono mb-2">Where each value is used in the Quote Builder</div>
                <div className="space-y-1.5 text-[11px] font-mono">
                  <div className="flex items-start gap-2"><span className="text-[#C8A46B] min-w-[150px]">BCProduct.cost_price</span><span className="text-slate-400">→ line <b className="text-slate-200">Cost ex-VAT</b> (margin floor)</span></div>
                  <div className="flex items-start gap-2"><span className="text-[#C8A46B] min-w-[150px]">BCProduct.price <span className="text-slate-500">(inc VAT)</span></span><span className="text-slate-400">→ ÷ 1.20 = <b className="text-slate-200">Sell ex-VAT</b> base the quote line opens at</span></div>
                  <div className="flex items-start gap-2"><span className="text-[#C8A46B] min-w-[150px]">retail_price</span><span className="text-slate-400">→ shown as <b className="text-slate-200">RRP / MSRP</b> reference</span></div>
                  <div className="flex items-start gap-2"><span className="text-[#C8A46B] min-w-[150px]">Tier discount %</span><span className="text-slate-400">→ applied off <b className="text-slate-200">Sell ex-VAT</b> per the customer's VIP tier (table below)</span></div>
                </div>
              </div>
              {(bc.bulk_pricing_rules || []).length > 0 && (
                <div className="mt-5 pt-5 border-t border-white/5">
                  <div className="text-[10px] uppercase tracking-widest text-slate-500 font-mono mb-2">Bulk pricing tiers</div>
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="text-[11px] uppercase tracking-widest text-slate-500 font-mono border-b border-white/10">
                          <th className="text-left py-2 pr-4">Min qty</th>
                          <th className="text-left py-2 pr-4">Max qty</th>
                          <th className="text-left py-2 pr-4">Type</th>
                          <th className="text-left py-2 pr-4">Amount</th>
                        </tr>
                      </thead>
                      <tbody className="font-mono">
                        {bc.bulk_pricing_rules.map((r, i) => (
                          <tr key={`bulk-${r.quantity_min ?? "min"}-${r.quantity_max ?? "max"}-${i}`} className="border-b border-white/5">
                            <td className="py-2 pr-4">{asText(r.quantity_min)}</td>
                            <td className="py-2 pr-4">{asText(r.quantity_max)}</td>
                            <td className="py-2 pr-4">{asText(r.type)}</td>
                            <td className="py-2 pr-4">{r.type === "percent" ? `${r.amount}%` : money(r.amount)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
              {(tm.BulkDiscountPrice1IncVAT || tm.BulkDiscountPrice2IncVAT || tm.BulkDiscountPrice3IncVAT) && (
                <div className="mt-5 pt-5 border-t border-white/5">
                  <div className="text-[10px] uppercase tracking-widest text-slate-500 font-mono mb-2">TM Bulk discount ladder</div>
                  <div className="grid grid-cols-3 gap-3">
                    {[1,2,3].map((n) => (
                      <div key={n} className="rounded-lg border border-white/10 p-3">
                        <div className="text-[10px] uppercase tracking-widest text-slate-500 font-mono">Tier {n} @ {asText(tm[`BulkDiscountCount${n}`])}+ units</div>
                        <div className="mt-1 font-mono text-slate-100">{asText(tm[`BulkDiscountPrice${n}IncVAT`])}</div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </Panel>

            {/* Tier pricing (Trade Account Discounts — Task Module 07) */}
            {tierPricing && (tierPricing.tiers || []).length > 0 && (
              <Panel
                icon={BadgePercent}
                title="Tier pricing"
                right={<Link to="/config/trade-discounts" className="text-[11px] font-mono text-orange-300 hover:underline">Edit discounts →</Link>}
                testid="panel-tier-pricing"
              >
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-4 mb-4">
                  <KV label="Retail ex VAT" value={<span className="font-mono text-[#C8A46B]">{money(tierPricing.base_ex_vat)}</span>} />
                  <KV label="Retail inc VAT" value={<span className="font-mono">{money(tierPricing.retail_inc_vat)}</span>} />
                  <KV label="Cost ex VAT" value={<span className="font-mono">{money(tierPricing.cost_ex_vat)}</span>} />
                </div>
                <div className="overflow-x-auto">
                  <table className="w-full text-sm" data-testid="tier-pricing-table">
                    <thead>
                      <tr className="text-[10px] uppercase tracking-widest text-slate-500 font-mono border-b border-white/10">
                        <th className="text-left py-2 pr-4">Tier</th>
                        <th className="text-right py-2 pr-4">Discount</th>
                        <th className="text-right py-2 pr-4">Price ex VAT</th>
                        <th className="text-right py-2 pr-4">Margin</th>
                      </tr>
                    </thead>
                    <tbody className="font-mono">
                      {tierPricing.tiers.map((t) => (
                        <tr key={t.tier} className="border-b border-white/5" data-testid={`tier-pricing-row-${t.tier}`}>
                          <td className="py-2 pr-4 font-semibold text-slate-100">{t.tier}</td>
                          <td className="py-2 pr-4 text-right text-slate-300">
                            {t.custom ? <span className="text-violet-300">Custom</span> : `${t.discount_pct}%`}
                          </td>
                          <td className="py-2 pr-4 text-right text-[#C8A46B]">{money(t.tier_price)}</td>
                          <td className="py-2 pr-4 text-right">
                            {t.margin_pct === null || t.margin_pct === undefined ? (
                              <span className="text-slate-500">—</span>
                            ) : (
                              <span className={t.margin_pct >= 6 ? "text-emerald-400" : t.margin_pct >= 3 ? "text-amber-400" : "text-red-400"}>
                                {t.margin_pct.toFixed(1)}%
                              </span>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                <div className="mt-3 text-[10px] font-mono text-slate-500">
                  Discount off the live retail price (ex VAT). Black is set per line in the Quote Builder.
                </div>
              </Panel>
            )}

            {/* Custom Fields */}
            <Panel icon={Layers} title="Custom fields" right={`${(bc.custom_fields || []).length} attributes`} testid="panel-customfields">
              {(bc.custom_fields || []).length === 0 ? (
                <div className="text-sm text-slate-500">No custom fields on this product.</div>
              ) : (
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-3">
                  {bc.custom_fields.map((f, i) => (
                    <div key={`cf-${f.id ?? f.name ?? i}`} className="flex items-start gap-3 py-1 border-b border-white/[0.04]">
                      <div className="text-[11px] uppercase tracking-widest text-slate-500 font-mono min-w-[140px] shrink-0">{f.name}</div>
                      <div className="text-sm text-slate-100 break-words">{f.value}</div>
                    </div>
                  ))}
                </div>
              )}
            </Panel>

            {/* Suppliers */}
            <Panel icon={Building2} title="Suppliers" right={`${(tm.ProductSuppliers || []).length} rows`} testid="panel-suppliers">
              {(tm.ProductSuppliers || []).length === 0 ? (
                <div className="text-sm text-slate-500">No suppliers linked.</div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full text-sm min-w-[720px]">
                    <thead>
                      <tr className="text-[11px] uppercase tracking-widest text-slate-500 font-mono border-b border-white/10">
                        <th className="text-left py-2 pr-4">Supplier</th>
                        <th className="text-left py-2 pr-4">Their SKU</th>
                        <th className="text-left py-2 pr-4">Cost</th>
                        <th className="text-left py-2 pr-4">Lead time</th>
                        <th className="text-left py-2 pr-4">Notes</th>
                      </tr>
                    </thead>
                    <tbody>
                      {tm.ProductSuppliers.map((s, i) => (
                        <tr key={`ps-${s.SupplierProductCode ?? s.SupplierName ?? "row"}-${i}`} className="border-b border-white/5">
                          <td className="py-2 pr-4 text-slate-100">{asText(s.SupplierName)}</td>
                          <td className="py-2 pr-4 font-mono text-slate-300">{asText(s.SupplierProductCode)}</td>
                          <td className="py-2 pr-4 font-mono">{s.SupplierCost ? money(s.SupplierCost) : asText(s.SupplierPrice) || "—"}</td>
                          <td className="py-2 pr-4 font-mono text-slate-300">{asText(s.SupplierLeadTime || s.LeadTime)}</td>
                          <td className="py-2 pr-4 text-slate-400 text-xs">{asText(s.Notes || s.SupplierNotes)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </Panel>

            {/* Marketplace Listings */}
            <Panel icon={Store} title="Marketplace listings" testid="panel-marketplace">
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                {[
                  { key: "AZ", label: "Amazon", data: tm.AZListing || {}, code: "ASIN" },
                  { key: "EB", label: "eBay",   data: tm.EBListing || {}, code: "ItemNumber" },
                  { key: "OB", label: "OnBuy",  data: tm.OBListing || {}, code: "OPC" },
                ].map((m) => (
                  <div key={m.key} className="rounded-lg border border-white/10 p-4">
                    <div className="flex items-center justify-between">
                      <div className="font-bold text-white">{m.label}</div>
                      <Chip tone={listedTone(m.data.Listed)}>{m.data.Listed ? "Listed" : "Not listed"}</Chip>
                    </div>
                    <div className="mt-3 space-y-2">
                      <div className="text-[10px] uppercase tracking-widest text-slate-500 font-mono">{m.code}</div>
                      <div className="font-mono text-sm text-slate-200 break-all">{asText(m.data[m.code])}</div>
                      {m.data.Shop && <div className="text-xs text-slate-400 font-mono">Shop · {m.data.Shop}</div>}
                      {m.data.CreatedProduct && <div className="text-xs text-slate-500 font-mono">Created {new Date(m.data.CreatedProduct).toLocaleDateString("en-GB")}</div>}
                    </div>
                  </div>
                ))}
              </div>
            </Panel>

            {/* Description */}
            {desc && (
              <Panel icon={FileText} title="Description" testid="panel-description">
                <div
                  className="prose prose-invert max-w-none text-sm text-slate-200 [&_p]:my-2 [&_ul]:my-2 [&_li]:ml-6 [&_a]:text-[#C8A46B] max-h-[32rem] overflow-y-auto pr-2"
                  dangerouslySetInnerHTML={{ __html: desc }}
                />
              </Panel>
            )}
          </div>

          {/* RIGHT (sidebar) */}
          <div className="space-y-6">
            {/* Inventory & physical */}
            <Panel icon={Boxes} title="Inventory & physical" testid="panel-inventory">
              <div className="grid grid-cols-2 gap-4">
                <KV label="Stock qty"        value={<span className="font-mono">{asText(bc.inventory_level)}</span>} />
                <KV label="Low-stock alert"  value={<span className="font-mono">{asText(bc.inventory_warning_level)}</span>} />
                <KV label="Tracking"         value={<span className="font-mono">{asText(bc.inventory_tracking)}</span>} />
                <KV label="Bin"              value={<span className="font-mono">{asText(bc.bin_picking_number)}</span>} />
                <KV label="Delivery msg"     value={<span className="font-mono">{asText(bc.availability_description)}</span>} wide />
                <KV label="Free shipping"    value={yesNo(bc.is_free_shipping)} />
                <KV label="Fixed shipping £" value={<span className="font-mono">{Number(bc.fixed_cost_shipping_price) > 0 ? money(bc.fixed_cost_shipping_price) : "—"}</span>} />
                <KV label="Weight"           value={<span className="font-mono">{asText(bc.weight)}</span>} />
                <KV label="W × D × H"        value={<span className="font-mono">{`${asText(bc.width)} × ${asText(bc.depth)} × ${asText(bc.height)}`}</span>} />
                <KV label="Condition"        value={asText(bc.condition)} />
                <KV label="Min / max qty"    value={<span className="font-mono">{`${asText(bc.order_quantity_minimum)} / ${asText(bc.order_quantity_maximum)}`}</span>} />
                <KV label="Preorder only"    value={yesNo(bc.is_preorder_only)} />
                <KV label="Sort order"       value={<span className="font-mono">{asText(bc.sort_order)}</span>} />
              </div>
            </Panel>

            {/* Media */}
            <Panel icon={ImgIcon} title="Media"
              right={`${(bc.images || []).length} img · ${(bc.videos || []).length} vid`}
              testid="panel-media"
            >
              {(bc.images || []).length === 0 && (bc.videos || []).length === 0 ? (
                <div className="text-sm text-slate-500">No media attached.</div>
              ) : (
                <>
                  {(bc.images || []).length > 0 && (
                    <div className="grid grid-cols-3 gap-2">
                      {bc.images.slice(0, 9).map((im, i) => {
                        const src = im.url_thumbnail || im.url_standard || im.url_tiny || im.image_file;
                        return (
                          <a key={`img-${im.id ?? src ?? i}`} href={im.url_zoom || im.url_standard || "#"} target="_blank" rel="noreferrer"
                             className="block aspect-square rounded-lg overflow-hidden bg-black/40 border border-white/10">
                            {src ? (
                              <img src={src} alt={im.description || "product image"} className="w-full h-full object-cover" loading="lazy" />
                            ) : (
                              <div className="w-full h-full grid place-items-center text-slate-600 text-xs">No src</div>
                            )}
                          </a>
                        );
                      })}
                    </div>
                  )}
                  {(bc.videos || []).length > 0 && (
                    <div className="mt-4 space-y-2">
                      {bc.videos.map((v, i) => (
                        <a key={`vid-${v.id ?? v.url ?? i}`} href={v.url} target="_blank" rel="noreferrer" className="flex items-center gap-2 text-sm text-[#C8A46B] hover:underline">
                          <ExternalLink className="w-3.5 h-3.5" />
                          <span className="truncate">{v.title || v.description || v.url}</span>
                        </a>
                      ))}
                    </div>
                  )}
                </>
              )}
            </Panel>

            {/* SEO & Social */}
            <Panel icon={SearchIcon} title="SEO & social" testid="panel-seo">
              <div className="space-y-3">
                <KV label="Page title"       value={asText(bc.page_title)} />
                <KV label="Meta description" value={asText(bc.meta_description)} />
                <KV label="Meta keywords"    value={(bc.meta_keywords || []).length > 0 ? bc.meta_keywords.join(", ") : "—"} />
                <KV label="Search keywords"  value={asText(bc.search_keywords)} />
                <div className="pt-2 border-t border-white/5">
                  <KV label="OG type"          value={asText(bc.open_graph_type)} />
                  <KV label="OG title"         value={asText(bc.open_graph_title)} />
                  <KV label="OG description"   value={asText(bc.open_graph_description)} />
                </div>
              </div>
            </Panel>

            {/* Talmia meta */}
            <Panel icon={Settings2} title="Talmia meta" testid="panel-tm">
              <div className="grid grid-cols-2 gap-4">
                <KV label="Status"     value={<ProductStatusPill value={tm.Status} />} />
                <KV label="Product type" value={productTypeLabel(tm.ProductType)} />
                <KV label="Xero ID"    value={<span className="font-mono text-xs break-all">{asText(tm.XeroId)}</span>} wide />
                <KV label="MM part"    value={<span className="font-mono">{asText(tm.MMPartCode)}</span>} />
                <KV label="AL part"    value={<span className="font-mono">{asText(tm.ALPartCode)}</span>} />
                <KV label="IS part"    value={<span className="font-mono">{asText(tm.ISPartCode)}</span>} />
                <KV label="MM URL"     value={tm.MMUrl ? <a className="text-[#C8A46B] underline break-all" href={tm.MMUrl} target="_blank" rel="noreferrer">{tm.MMUrl}</a> : "—"} wide />
                <KV label="MM cat path" value={<span className="font-mono text-xs">{asText(tm.MMCatPath)}</span>} wide />
                <KV label="MM variant" value={asText(tm.MMVariantDetails)} />
                <KV label="Updated"    value={tm.UpdatedDate ? new Date(tm.UpdatedDate).toLocaleString("en-GB") : "—"} />
              </div>
            </Panel>

            {/* Notes */}
            {tm.Notes && (
              <Panel icon={FileText} title="Notes / history" testid="panel-notes">
                <pre className="whitespace-pre-wrap text-xs text-slate-300 leading-relaxed font-mono">{tm.Notes}</pre>
              </Panel>
            )}
          </div>
        </div>
      </div>
    
      <LinkedRecords collection="products" id={id} />
      <div className="mt-6" data-testid="lab-product-rawjson-wrap">
        <RawJson
          title="Raw record — expand, search & copy"
          testid="lab-product-rawjson"
          data={doc}
          sources={[
            { key: "full", label: "Full record", data: doc },
            { key: "bc",   label: "BigCommerce · BCProduct", data: bc },
            { key: "tm",   label: "Talmia · TMProduct", data: tm },
          ]}
        />
      </div>
      <DataAudit doc={doc} title="Data audit — full record" testid="lab-data-audit" />
    </Layout>
  );
}
